import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api, API_BASE_URL } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';
import { 
  CodeBracketIcon, 
  ClipboardDocumentIcon, 
  PlayIcon,
  EyeIcon,
  EyeSlashIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  SignalIcon,
  MegaphoneIcon,
  MapPinIcon,
  UserGroupIcon,
  MicrophoneIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  PhoneIcon,
  GlobeAltIcon,
  PlusIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const APIDocs = () => {
  const { user } = useAuth();
  const [selectedEndpoint, setSelectedEndpoint] = useState('send-message');
  const [testData, setTestData] = useState({});
  const [testResponse, setTestResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [activeCodeTab, setActiveCodeTab] = useState('curl');
  
  // Voice message specific states
  const [voiceFile, setVoiceFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  
  // Poll specific states
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [allowMultipleAnswers, setAllowMultipleAnswers] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  // Reset form when endpoint changes
  useEffect(() => {
    setTestData({});
    setVoiceFile(null);
    setPollOptions(['', '']);
    setAllowMultipleAnswers(false);
    setTestResponse(null);
    
    // Clear file input if it exists
    const fileInput = document.getElementById('voice-file-input');
    if (fileInput) fileInput.value = '';
  }, [selectedEndpoint]);

  const fetchAgents = async () => {
    try {
      const response = await api.getWhatsAppClients();
      const clientsData = response.data?.clients || {};
      setAgents(clientsData);
      // Don't auto-select an agent - let user choose
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // Prepare WhatsApp clients data for SearchableSelect
  const agentOptions = agents && Object.keys(agents).length > 0
    ? Object.values(agents)
        .filter(client => client.status === 'active')
        .map((client) => ({
          value: client.id,
          label: client.agent_name || client.display_name || `Client ${client.client_id}`,
          description: client.whatsapp_number || client.client_id,
          icon: '📱',
          port: client.port,
          webhookApiKey: client.webhook_api_key || client.api_key,
          clientId: client.client_id
        }))
    : [];

  const apiEndpoints = [
    {
      id: 'send-message',
      name: 'Send Text Message',
      icon: ChatBubbleLeftRightIcon,
      method: 'POST',
      endpoint: '/api/v1/send/text',
      description: 'Send a text message to a WhatsApp contact',
      parameters: [
        { name: 'chat_id', type: 'string', required: true, description: 'Recipient Chat ID - Phone number in international format (without +)', placeholder: '233554694531' },
        { name: 'text', type: 'string', required: true, description: 'Message Content - The text message you want to send', placeholder: 'Type your message here...' }
      ],
      example: {
        chat_id: '233554694531',
        text: 'Hello! This is a test message from our API.'
      }
    },
    {
      id: 'send-voice',
      name: 'Send Voice Message',
      icon: SignalIcon,
      method: 'POST',
      endpoint: '/api/v1/send/voice',
      description: 'Send a voice message to a WhatsApp contact',
      parameters: [
        { name: 'chat_id', type: 'string', required: true, description: 'Recipient Chat ID - Phone number in international format (without +)', placeholder: '233554694531' },
        { name: 'file', type: 'object', required: true, description: 'Audio file object with url or base64 data' }
      ],
      example: {
        chat_id: '233554694531',
        file: { url: 'https://example.com/voice_recording.mp3' }
      }
    },
    {
      id: 'send-location',
      name: 'Send Location',
      icon: MapPinIcon,
      method: 'POST',
      endpoint: '/api/v1/send/location',
      description: 'Send a location to a WhatsApp contact',
      parameters: [
        { name: 'chat_id', type: 'string', required: true, description: 'Recipient Chat ID - Phone number in international format (without +)', placeholder: '233554694531' },
        { name: 'latitude', type: 'number', required: true, description: 'Location latitude', placeholder: '5.6037' },
        { name: 'longitude', type: 'number', required: true, description: 'Location longitude', placeholder: '-0.1870' },
        { name: 'title', type: 'string', required: false, description: 'Location name/title', placeholder: 'Accra, Ghana' }
      ],
      example: {
        chat_id: '233554694531',
        latitude: 5.6037,
        longitude: -0.1870,
        title: 'Accra, Ghana'
      }
    },
    {
      id: 'send-poll',
      name: 'Send Poll',
      icon: ChatBubbleLeftRightIcon,
      method: 'POST',
      endpoint: '/api/v1/send/poll',
      description: 'Send a poll to a WhatsApp contact',
      parameters: [
        { name: 'chat_id', type: 'string', required: true, description: 'Recipient Chat ID - Phone number in international format (without +)', placeholder: '233554694531' },
        { name: 'poll', type: 'object', required: true, description: 'Poll data with name and options' }
      ],
      example: {
        chat_id: '233554694531',
        poll: {
          name: 'What is your favorite color?',
          options: ['Red', 'Blue', 'Green', 'Yellow'],
          selectableCount: 1
        }
      }
    },
    {
      id: 'send-file',
      name: 'Send File',
      icon: MegaphoneIcon,
      method: 'POST',
      endpoint: '/api/v1/send/file',
      description: 'Send a file to a WhatsApp contact',
      parameters: [
        { name: 'chat_id', type: 'string', required: true, description: 'Recipient Chat ID - Phone number in international format (without +)', placeholder: '233554694531' },
        { name: 'file', type: 'object', required: true, description: 'File object with url or base64 data' },
        { name: 'caption', type: 'string', required: false, description: 'Optional caption for the file', placeholder: 'Here is the document you requested' }
      ],
      example: {
        chat_id: '233554694531',
        file: { url: 'https://example.com/document.pdf' },
        caption: 'Here is the document you requested'
      }
    },
    {
      id: 'get-groups',
      name: 'Get Groups',
      icon: UserGroupIcon,
      method: 'GET',
      endpoint: '/api/v1/send/groups',
      description: 'Get all WhatsApp groups that the agent belongs to',
      parameters: [
        { name: 'limit', type: 'number', required: false, description: 'Maximum number of groups to return (default: 100)', placeholder: '100' },
        { name: 'offset', type: 'number', required: false, description: 'Number of groups to skip for pagination (default: 0)', placeholder: '0' }
      ],
      example: {}
    },
    {
      id: 'client-info',
      name: 'Get Client Info',
      icon: InformationCircleIcon,
      method: 'GET',
      endpoint: '/api/v1/client/info',
      description: 'Get information about your WhatsApp client',
      parameters: [],
      example: {}
    }
  ];

  const selectedEndpointData = apiEndpoints.find(endpoint => endpoint.id === selectedEndpoint);

  const handleTestEndpoint = async () => {
    if (!selectedAgent) {
      toast.error('Please select an agent first');
      return;
    }

    // Get the selected agent's API key
    const selectedAgentData = Object.values(agents).find(agent => agent.id === selectedAgent);
    const apiKey = selectedAgentData?.webhook_api_key || selectedAgentData?.api_key;
    
    if (!apiKey) {
      toast.error('API key not found for selected agent');
      return;
    }

    setLoading(true);
    try {
      let response;
      
      // Call the external API based on the selected endpoint
      switch (selectedEndpoint) {
        case 'send-message':
          if (!testData.chat_id || !testData.text) {
            toast.error('Please fill in chat_id and text fields');
            return;
          }
          response = await api.testExternalTextMessage(
            apiKey,
            testData.chat_id,
            testData.text
          );
          break;
          
        case 'send-location':
          if (!testData.chat_id || !testData.latitude || !testData.longitude) {
            toast.error('Please fill in chat_id, latitude, and longitude fields');
            return;
          }
          response = await api.testExternalLocationMessage(
            apiKey,
            testData.chat_id,
            parseFloat(testData.latitude),
            parseFloat(testData.longitude),
            testData.title || ''
          );
          break;
          
        case 'send-poll':
          const validOptions = pollOptions.filter(opt => opt.trim() !== '');
          if (!testData.chat_id || !testData.poll?.name || validOptions.length < 2) {
            toast.error('Please fill in chat_id, poll question, and at least 2 poll options');
            return;
          }
          
          const pollData = {
            name: testData.poll.name,
            options: validOptions,
            selectableCount: allowMultipleAnswers ? validOptions.length : 1
          };
          
          response = await api.testExternalPollMessage(
            apiKey,
            testData.chat_id,
            pollData
          );
          break;
          
        case 'send-voice':
          if (!testData.chat_id || !voiceFile) {
            toast.error('Please fill in chat_id and select a voice file');
            return;
          }
          
          try {
            // Upload file and get URL
            const formData = new FormData();
            formData.append('file', voiceFile);
            
            const uploadResponse = await fetch(`${API_BASE_URL}/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: formData
            });
            
            if (!uploadResponse.ok) {
              throw new Error('Failed to upload file');
            }
            
            const fileData = await uploadResponse.json();
            
            response = await api.testExternalVoiceMessage(
              apiKey,
              testData.chat_id,
              {
                url: fileData.url,
                mimetype: fileData.mimetype
              }
            );
          } catch (uploadError) {
            toast.error('Failed to upload voice file: ' + uploadError.message);
            return;
          }
          break;
          
        case 'send-file':
          toast.error('File upload testing requires a file selection interface. Use the Actions > Send File page for testing file uploads.');
          return;
          
        case 'get-groups':
          // Extract limit and offset parameters if provided
          const groupParams = {};
          if (testData.limit) groupParams.limit = testData.limit;
          if (testData.offset) groupParams.offset = testData.offset;
          response = await api.testExternalGroups(apiKey, groupParams);
          break;
          
        case 'client-info':
          response = await api.testExternalClientInfo(apiKey);
          break;
          
        default:
          toast.error('Endpoint not supported for testing');
          return;
      }

      setTestResponse(response.data);
      toast.success('API test completed successfully!');
    } catch (error) {
      console.error('Test error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      toast.error(errorMessage || 'API test failed');
      setTestResponse({ 
        error: errorMessage || error.message,
        status: error.response?.status,
        details: error.response?.data 
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const generateCurlCommand = () => {
    const endpoint = selectedEndpointData;
    const payload = { ...testData };
    
    // Get the selected agent's API key
    const selectedAgentData = Object.values(agents).find(agent => agent.id === selectedAgent);
    const apiKey = selectedAgentData?.webhook_api_key || selectedAgentData?.api_key || 'YOUR_API_KEY';
    
    return `curl -X ${endpoint.method} \\
  ${API_BASE_URL}${endpoint.endpoint} \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '${JSON.stringify(payload, null, 2)}'`;
  };

  const generateCodeExample = (language) => {
    const endpoint = selectedEndpointData;
    const payload = { ...testData };
    
    // Get the selected agent's API key
    const selectedAgentData = Object.values(agents).find(agent => agent.id === selectedAgent);
    const apiKey = selectedAgentData?.webhook_api_key || selectedAgentData?.api_key || 'YOUR_API_KEY';

    if (language === 'javascript') {
      return `const response = await fetch('${API_BASE_URL}${endpoint.endpoint}', {
  method: '${endpoint.method}',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': '${apiKey}'
  },
  body: JSON.stringify(${JSON.stringify(payload, null, 4)})
});

const data = await response.json();
console.log(data);`;
    }

    if (language === 'python') {
      return `import requests

url = "${API_BASE_URL}${endpoint.endpoint}"
headers = {
    "Content-Type": "application/json",
    "X-API-Key": "${apiKey}"
}
payload = ${JSON.stringify(payload, null, 4)}

response = requests.${endpoint.method.toLowerCase()}(url, headers=headers, json=payload)
data = response.json()
print(data)`;
    }

    if (language === 'php') {
      return `<?php
$url = "${API_BASE_URL}${endpoint.endpoint}";
$headers = [
    'Content-Type: application/json',
    'X-API-Key: ${apiKey}'
];
$payload = json_encode(${JSON.stringify(payload, null, 4)});

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);

$data = json_decode($response, true);
print_r($data);
?>`;
    }

    if (language === 'node') {
      return `const axios = require('axios');

const config = {
  method: '${endpoint.method.toLowerCase()}',
  url: '${API_BASE_URL}${endpoint.endpoint}',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': '${apiKey}'
  },
  data: ${JSON.stringify(payload, null, 4)}
};

axios(config)
  .then(response => {
    console.log(response.data);
  })
  .catch(error => {
    console.error(error);
  });`;
    }

    return '';
  };

  // Voice message helper functions
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/x-m4a'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid audio file (MP3, WAV, OGG, M4A)');
        return;
      }

      // Validate file size (16MB max)
      const maxSize = 16 * 1024 * 1024; // 16MB in bytes
      if (file.size > maxSize) {
        toast.error('File size must be less than 16MB');
        return;
      }

      setVoiceFile(file);
      toast.success('Voice file selected successfully!');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/ogg; codecs=opus' });
        const file = new File([blob], `recording_${Date.now()}.ogg`, { type: 'audio/ogg; codecs=opus' });
        setVoiceFile(file);
        toast.success('Recording saved successfully!');

        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      toast.info('Recording started...');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      toast.info('Recording stopped');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Location helper functions
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setTestData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
          toast.success('Current location detected successfully!');
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Failed to get current location. Please check your browser permissions.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser');
    }
  };

  // Poll helper functions
  const addPollOption = () => {
    if (pollOptions.length < 12) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const removePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <CodeBracketIcon className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">API Documentation</h1>
          </div>
          <p className="text-lg text-gray-600">
            Integrate your WhatsApp agents into your own platform using our REST API. These endpoints allow you to send messages through your connected WhatsApp agents programmatically.
          </p>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <SparklesIcon className="h-5 w-5 text-blue-600 mt-0.5" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-blue-800">How it Works</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Your API calls are authenticated using your WhatsApp agent's API key, validated against your active agents, and then forwarded to the appropriate agent for execution.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - API Endpoints */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">API Endpoints</h2>
              <nav className="space-y-2">
                {apiEndpoints.map((endpoint) => (
                  <button
                    key={endpoint.id}
                    onClick={() => setSelectedEndpoint(endpoint.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedEndpoint === endpoint.id
                        ? 'bg-blue-100 text-blue-700 border-l-2 border-blue-500'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <endpoint.icon className="h-4 w-4" />
                      <span>{endpoint.name}</span>
                    </div>
                  </button>
                ))}
              </nav>

              {/* Base URL Info */}
              <div className="mt-8 p-4 bg-green-50 rounded-lg">
                <h3 className="text-sm font-medium text-green-800 mb-2">Base URL</h3>
                <div className="bg-green-100 p-2 rounded text-xs font-mono">
                  {API_BASE_URL}
                </div>
                <p className="text-xs text-green-700 mt-2">
                  All API endpoints are prefixed with this base URL.
                </p>
              </div>

              {/* Authentication Info */}
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">Authentication</h3>
                <p className="text-xs text-yellow-700 mb-2">
                  All API requests require authentication using your agent's API key:
                </p>
                <div className="bg-yellow-100 p-2 rounded text-xs font-mono">
                  X-API-Key: YOUR_API_KEY
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  Select an agent below to view and copy your API key.
                </p>
              </div>

              {/* Response Format */}
              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <h3 className="text-sm font-medium text-purple-800 mb-2">Response Format</h3>
                <p className="text-xs text-purple-700 mb-2">
                  All successful responses follow this format:
                </p>
                <div className="bg-purple-100 p-2 rounded text-xs font-mono">
                  {JSON.stringify({
                    success: true,
                    message: "Operation completed",
                    result: "..."
                  }, null, 2)}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Endpoint Details */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <selectedEndpointData.icon className="h-6 w-6 text-blue-600" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedEndpointData.name}
                  </h2>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    selectedEndpointData.method === 'POST' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {selectedEndpointData.method}
                  </span>
                </div>
                <p className="text-gray-600 mt-2">{selectedEndpointData.description}</p>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Endpoint</h3>
                  <div className="bg-gray-100 p-3 rounded-lg font-mono text-sm">
                    {selectedEndpointData.method} {selectedEndpointData.endpoint}
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Parameter
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Required
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedEndpointData.parameters.map((param, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {param.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                                {param.type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className={`px-2 py-1 rounded text-xs ${
                                param.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {param.required ? 'Required' : 'Optional'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {param.description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* API Test Tool */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                  <PlayIcon className="h-5 w-5 text-green-600" />
                  <span>Test API</span>
                </h2>
                <p className="text-gray-600 mt-1">
                  Test the API endpoint with your own data using real API calls
                </p>
              </div>

              <div className="p-6">
                {/* Agent Selection */}
                <div className="mb-6">
                  <SearchableSelect
                    label="WhatsApp Agent"
                    name="client"
                    options={agentOptions}
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    placeholder="Search and select a WhatsApp agent..."
                    isRequired={true}
                    isSearchable={true}
                    isClearable={true}
                  />
                </div>

                {/* API Key Display */}
                {selectedAgent && (
                  (() => {
                    // Find the selected agent by ID from the agents object
                    const selectedAgentData = Object.values(agents).find(agent => agent.id === selectedAgent);
                    const apiKey = selectedAgentData?.webhook_api_key || selectedAgentData?.api_key;
                    
                    return apiKey ? (
                      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className="h-2 w-2 bg-green-400 rounded-full"></div>
                            <h4 className="text-sm font-medium text-green-800">Your API Key</h4>
                          </div>
                          <button
                            onClick={() => copyToClipboard(apiKey)}
                            className="flex items-center space-x-1 px-2 py-1 text-xs text-green-700 hover:text-green-900 hover:bg-green-200 rounded transition-colors"
                          >
                            <ClipboardDocumentIcon className="h-3 w-3" />
                            <span>Copy</span>
                          </button>
                        </div>
                        <div className="bg-green-100 p-2 rounded font-mono text-xs break-all">
                          {apiKey}
                        </div>
                        <p className="text-xs text-green-700 mt-1">
                          Use this key in the X-API-Key header for authentication
                        </p>
                      </div>
                    ) : null;
                  })()
                )}

                {/* Test Form */}
                <div className="space-y-6 mb-6">
                  {/* Chat ID field for all endpoints except client-info and get-groups */}
                  {selectedEndpoint !== 'client-info' && selectedEndpoint !== 'get-groups' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Recipient Chat ID <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          value={testData.chat_id || ''}
                          onChange={(e) => setTestData(prev => ({ ...prev, chat_id: e.target.value }))}
                          placeholder="233554694531"
                          className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Phone number in international format (without +)</p>
                    </div>
                  )}

                  {/* Endpoint-specific forms */}
                  {selectedEndpoint === 'send-message' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message Text <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={testData.text || ''}
                        onChange={(e) => setTestData(prev => ({ ...prev, text: e.target.value }))}
                        placeholder="Type your message here..."
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                      />
                      <p className="text-xs text-gray-500 mt-1">The text message you want to send</p>
                    </div>
                  )}

                  {selectedEndpoint === 'send-voice' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Voice File <span className="text-red-500">*</span>
                      </label>

                      {/* Recording Controls */}
                      <div className="mb-4 flex space-x-3">
                        {!isRecording ? (
                          <button
                            type="button"
                            onClick={startRecording}
                            className="inline-flex items-center px-4 py-2 border border-orange-300 shadow-sm text-sm leading-4 font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
                          >
                            <MicrophoneIcon className="h-4 w-4 mr-2" />
                            Record Voice
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={stopRecording}
                            className="inline-flex items-center px-4 py-2 border border-orange-300 shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 transition-colors animate-pulse"
                          >
                            <MicrophoneIcon className="h-4 w-4 mr-2" />
                            Recording... Click to Stop
                          </button>
                        )}
                      </div>

                      {/* File Upload Area */}
                      {!voiceFile && (
                        <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 transition-colors hover:border-blue-400 focus-within:border-blue-500">
                          <div className="text-center">
                            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-4">
                              <label htmlFor="voice-file-input" className="cursor-pointer">
                                <span className="mt-2 block text-sm font-medium text-gray-900">
                                  Or upload an existing audio file
                                </span>
                                <span className="mt-1 block text-xs text-gray-500">
                                  MP3, WAV, OGG, M4A up to 16MB
                                </span>
                              </label>
                              <input
                                id="voice-file-input"
                                name="voice-file"
                                type="file"
                                accept="audio/*"
                                className="sr-only"
                                onChange={handleFileChange}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Selected File Display */}
                      {voiceFile && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center">
                            <DocumentIcon className="h-5 w-5 text-green-600 mr-3" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-green-800">{voiceFile.name}</p>
                              <p className="text-xs text-green-600">{formatFileSize(voiceFile.size)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setVoiceFile(null);
                                const fileInput = document.getElementById('voice-file-input');
                                if (fileInput) fileInput.value = '';
                              }}
                              className="text-green-600 hover:text-green-800"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedEndpoint === 'send-location' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-4">
                          Coordinates <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="relative">
                            <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="number"
                              step="any"
                              value={testData.latitude || ''}
                              onChange={(e) => setTestData(prev => ({ ...prev, latitude: parseFloat(e.target.value) }))}
                              className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="5.6037"
                              required
                            />
                            <label className="absolute -top-2 left-10 bg-white px-1 text-xs text-gray-600">Latitude</label>
                          </div>
                          <div className="relative">
                            <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="number"
                              step="any"
                              value={testData.longitude || ''}
                              onChange={(e) => setTestData(prev => ({ ...prev, longitude: parseFloat(e.target.value) }))}
                              className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="-0.1870"
                              required
                            />
                            <label className="absolute -top-2 left-10 bg-white px-1 text-xs text-gray-600">Longitude</label>
                          </div>
                        </div>

                        <div className="mt-4 text-center">
                          <button
                            type="button"
                            onClick={getCurrentLocation}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                          >
                            <MapPinIcon className="h-4 w-4 mr-2 text-red-500" />
                            Use Current Location
                          </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-2 text-center">
                          Click to automatically detect your current coordinates
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Title
                        </label>
                        <input
                          type="text"
                          value={testData.title || ''}
                          onChange={(e) => setTestData(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Location title"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Optional title for this location
                        </p>
                      </div>
                    </>
                  )}

                  {selectedEndpoint === 'send-poll' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Poll Question <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={testData.poll?.name || ''}
                          onChange={(e) => setTestData(prev => ({
                            ...prev,
                            poll: { ...prev.poll, name: e.target.value }
                          }))}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="What is your favorite color?"
                          required
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Enter a clear, engaging question for your poll
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <label className="block text-sm font-medium text-gray-700">
                            Poll Options <span className="text-red-500">*</span>
                          </label>
                          <button
                            type="button"
                            onClick={addPollOption}
                            disabled={pollOptions.length >= 12}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Option
                          </button>
                        </div>

                        <div className="space-y-3">
                          {pollOptions.map((option, index) => (
                            <div key={index} className="flex items-center space-x-3">
                              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                              </div>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => updatePollOption(index, e.target.value)}
                                className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Option ${index + 1}`}
                                required
                              />
                              {pollOptions.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => removePollOption(index)}
                                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        <div className="mt-3 flex items-center">
                          <input
                            type="checkbox"
                            id="multipleAnswers"
                            checked={allowMultipleAnswers}
                            onChange={(e) => setAllowMultipleAnswers(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="multipleAnswers" className="ml-2 block text-sm text-gray-700">
                            Allow multiple answers
                          </label>
                        </div>

                        <p className="text-sm text-gray-500 mt-2">
                          Minimum 2 options required, maximum 12 options allowed
                        </p>
                      </div>
                    </>
                  )}

                  {selectedEndpoint === 'send-file' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-blue-800">File Upload Testing</h4>
                          <p className="mt-1 text-sm text-blue-700">
                            To test file uploads, please use the Actions > Send File page which provides a complete file upload interface.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleTestEndpoint}
                  disabled={loading || !selectedAgent || 
                    (selectedEndpoint === 'send-message' && (!testData.chat_id || !testData.text)) ||
                    (selectedEndpoint === 'send-voice' && (!testData.chat_id || !voiceFile)) ||
                    (selectedEndpoint === 'send-location' && (!testData.chat_id || !testData.latitude || !testData.longitude)) ||
                    (selectedEndpoint === 'send-poll' && (!testData.chat_id || !testData.poll?.name || pollOptions.filter(opt => opt.trim()).length < 2))
                  }
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Testing...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-4 w-4 mr-2" />
                      Test API
                    </>
                  )}
                </button>

                {/* Test Response */}
                {testResponse && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Response</h3>
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                        {JSON.stringify(testResponse, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Code Examples */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Code Examples</h2>
                <p className="text-gray-600 mt-1">
                  Ready-to-use code snippets for your integration
                </p>
              </div>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-0">
                  {[
                    { id: 'curl', name: 'cURL', icon: '⚡' },
                    { id: 'javascript', name: 'JavaScript', icon: '🟨' },
                    { id: 'python', name: 'Python', icon: '🐍' },
                    { id: 'php', name: 'PHP', icon: '🐘' },
                    { id: 'node', name: 'Node.js', icon: '💚' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveCodeTab(tab.id)}
                      className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeCodeTab === tab.id
                          ? 'border-blue-500 text-blue-600 bg-blue-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg">{tab.icon}</span>
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                <div className="relative">
                  {/* Copy Button */}
                  <button
                    onClick={() => {
                      const code = activeCodeTab === 'curl' 
                        ? generateCurlCommand() 
                        : generateCodeExample(activeCodeTab);
                      copyToClipboard(code);
                    }}
                    className="absolute top-4 right-4 z-10 flex items-center space-x-1 px-3 py-1.5 text-xs text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-3 w-3" />
                    <span>Copy</span>
                  </button>

                  {/* Code Block */}
                  <div className="bg-gray-900 text-gray-100 p-6 rounded-lg overflow-x-auto">
                    <pre className="text-sm leading-relaxed">
                      {activeCodeTab === 'curl' 
                        ? generateCurlCommand() 
                        : generateCodeExample(activeCodeTab)
                      }
                    </pre>
                  </div>
                </div>

                {/* Code Description */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-xs font-bold">💡</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {activeCodeTab === 'curl' && (
                        <p>
                          <strong>cURL:</strong> Command-line tool for making HTTP requests. 
                          Run this command in your terminal to test the API directly.
                        </p>
                      )}
                      {activeCodeTab === 'javascript' && (
                        <p>
                          <strong>JavaScript (Fetch API):</strong> Modern browser-native method for making HTTP requests. 
                          Works in all modern browsers and Node.js (v18+).
                        </p>
                      )}
                      {activeCodeTab === 'python' && (
                        <p>
                          <strong>Python (Requests):</strong> Most popular Python library for HTTP requests. 
                          Install with <code className="bg-white px-1 rounded">pip install requests</code>.
                        </p>
                      )}
                      {activeCodeTab === 'php' && (
                        <p>
                          <strong>PHP (cURL):</strong> Built-in PHP extension for making HTTP requests. 
                          No additional installation required.
                        </p>
                      )}
                      {activeCodeTab === 'node' && (
                        <p>
                          <strong>Node.js (Axios):</strong> Popular HTTP client library for Node.js. 
                          Install with <code className="bg-white px-1 rounded">npm install axios</code>.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* API Key Notice */}
                {!selectedAgent && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="text-amber-600">⚠️</div>
                      <p className="text-sm text-amber-800">
                        <strong>Select an agent above</strong> to see your actual API key in the code examples.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIDocs;